import { InfiniteData, QueryClient, QueryKey } from '@tanstack/react-query'
import {
  getPostRow,
  isPostAccessError,
  postFeedQueryKey,
  postPinnedQueryKey,
  postRowEntityKey,
  postRowQueries,
  postRowsQueryKey,
  postScheduledQueryKey,
} from '../../api/posts'
import { discussionQueryKey } from '../../api/comments'
import { peopleQueryKey } from '../../api/people'
import {
  PostEventAttendance,
  PostPollCard,
  PostRow,
  PostsPage,
} from '../../generated/api/post-row'

type RowUpdate = (row: PostRow) => PostRow

export const applyToRows = (
  rows: PostRow[],
  postId: string,
  update: RowUpdate
): PostRow[] =>
  rows.some((row) => row.id === postId)
    ? rows.map((row) => (row.id === postId ? update(row) : row))
    : rows

export const dropRow = (rows: PostRow[], postId: string): PostRow[] =>
  rows.filter((row) => row.id !== postId)

export const likedRow = (row: PostRow, liked: boolean): PostRow =>
  row.likes.viewerLiked === liked
    ? row
    : {
        ...row,
        likes: {
          viewerLiked: liked,
          count: Math.max(0, row.likes.count + (liked ? 1 : -1)),
        },
      }

export const commentedRow = (row: PostRow, delta: number): PostRow => ({
  ...row,
  comments: {
    count: Math.max(0, row.comments.count + delta),
    viewerCommented: delta > 0 ? true : row.comments.viewerCommented,
  },
})

export const pinnedRow = (row: PostRow, pinned: boolean): PostRow =>
  row.isPinned === pinned ? row : { ...row, isPinned: pinned }

export const rowWithPoll = (row: PostRow, poll: PostPollCard): PostRow => ({
  ...row,
  polls: row.polls.map((card) => (card.id === poll.id ? poll : card)),
})

const mapPages = (
  data: InfiniteData<PostsPage, string | undefined> | undefined,
  mapItems: (items: PostRow[]) => PostRow[]
): InfiniteData<PostsPage, string | undefined> | undefined => {
  if (data == null) {
    return data
  }
  const pages = data.pages.map((page) => {
    const items = mapItems(page.items)
    return items === page.items ? page : { ...page, items }
  })
  return pages.every((page, index) => page === data.pages[index])
    ? data
    : { ...data, pages }
}

export const updatePostRow = (
  client: QueryClient,
  postId: string,
  update: RowUpdate
): void => {
  client.setQueriesData<InfiniteData<PostsPage, string | undefined>>(
    { queryKey: postFeedQueryKey },
    (data) => mapPages(data, (items) => applyToRows(items, postId, update))
  )
  client.setQueriesData<PostRow[]>({ queryKey: postPinnedQueryKey }, (rows) =>
    rows == null ? rows : applyToRows(rows, postId, update)
  )
  client.setQueryData<PostRow[]>(postScheduledQueryKey, (rows) =>
    rows == null ? rows : applyToRows(rows, postId, update)
  )
  client.setQueryData(postRowQueries.row(postId).queryKey, (row) =>
    row == null ? row : update(row)
  )
}

type AnsweredEvent = {
  attendanceEnabled?: boolean
  attendance?: {
    userId: string
    status: string
    comment?: string
  }[]
}

export const viewerAttendance = (
  event: AnsweredEvent,
  viewerId: string
): PostEventAttendance => {
  const enabled = event.attendanceEnabled === true
  const own = !enabled
    ? undefined
    : event.attendance?.find((attendance) => attendance.userId === viewerId)
  return {
    enabled,
    viewerStatus: own?.status as PostEventAttendance['viewerStatus'],
    viewerComment: own?.comment || undefined,
  }
}

const carriesEvent = (row: PostRow, eventId: string): boolean =>
  row.events.some((event) => event.id === eventId)

export const rowWithEventAttendance = (
  row: PostRow,
  eventId: string,
  attendance: PostEventAttendance
): PostRow =>
  carriesEvent(row, eventId)
    ? {
        ...row,
        events: row.events.map((event) =>
          event.id === eventId ? { ...event, attendance } : event
        ),
      }
    : row

export const updateEventAttendance = (
  client: QueryClient,
  eventId: string,
  attendance: PostEventAttendance
): void => {
  const update = (rows: PostRow[]): PostRow[] =>
    rows.some((row) => carriesEvent(row, eventId))
      ? rows.map((row) => rowWithEventAttendance(row, eventId, attendance))
      : rows
  client.setQueriesData<InfiniteData<PostsPage, string | undefined>>(
    { queryKey: postFeedQueryKey },
    (data) => mapPages(data, update)
  )
  client.setQueriesData<PostRow[]>({ queryKey: postPinnedQueryKey }, (rows) =>
    rows == null ? rows : update(rows)
  )
  client.setQueriesData<PostRow>({ queryKey: postRowEntityKey }, (row) =>
    row == null ? row : rowWithEventAttendance(row, eventId, attendance)
  )
}

export type PinnedSnapshot = ReturnType<typeof snapshotPinnedRows>

export const snapshotPinnedRows = (
  client: QueryClient
): [QueryKey, PostRow[] | undefined][] =>
  client.getQueriesData<PostRow[]>({ queryKey: postPinnedQueryKey })

export const restorePinnedRows = (
  client: QueryClient,
  snapshot: PinnedSnapshot
): void => {
  snapshot.forEach(([key, rows]) => client.setQueryData<PostRow[]>(key, rows))
}

export const removePinnedRow = (client: QueryClient, postId: string): void => {
  client.setQueriesData<PostRow[]>({ queryKey: postPinnedQueryKey }, (rows) =>
    rows == null ? rows : dropRow(rows, postId)
  )
}

export const removePostFromLists = (
  client: QueryClient,
  postId: string
): void => {
  client.setQueriesData<InfiniteData<PostsPage, string | undefined>>(
    { queryKey: postFeedQueryKey },
    (data) => mapPages(data, (items) => dropRow(items, postId))
  )
  client.setQueriesData<PostRow[]>({ queryKey: postPinnedQueryKey }, (rows) =>
    rows == null ? rows : dropRow(rows, postId)
  )
  client.setQueryData<PostRow[]>(postScheduledQueryKey, (rows) =>
    rows == null ? rows : dropRow(rows, postId)
  )
}

export const removePostRow = (client: QueryClient, postId: string): void => {
  removePostFromLists(client, postId)
  client.removeQueries({ queryKey: postRowQueries.row(postId).queryKey })
}

export const withdrawPost = (client: QueryClient, postId: string): void => {
  removePostFromLists(client, postId)
  client.resetQueries({ queryKey: postRowQueries.row(postId).queryKey })
  client.removeQueries({
    queryKey: discussionQueryKey(postId),
    predicate: (query) => query.getObserversCount() === 0,
  })
  client.removeQueries({
    queryKey: peopleQueryKey({ kind: 'post-likers', id: postId }),
  })
  client.removeQueries({
    queryKey: peopleQueryKey({ kind: 'post-readers', id: postId }),
  })
}

export const refreshPostRow = (
  client: QueryClient,
  postId: string
): Promise<void> =>
  getPostRow(postId).then(
    (response) => updatePostRow(client, postId, () => response.data.data),
    (error) => {
      if (isPostAccessError(error)) {
        withdrawPost(client, postId)
      }
    }
  )

export const invalidatePostRows = (client: QueryClient): void => {
  client.invalidateQueries({ queryKey: postRowsQueryKey })
}

export const cancelPostRows = async (
  client: QueryClient
): Promise<QueryKey[]> => {
  const interrupted = client
    .getQueryCache()
    .findAll({
      queryKey: postRowsQueryKey,
      predicate: (query) => query.state.fetchStatus !== 'idle',
    })
    .map((query) => query.queryKey)
  await client.cancelQueries({ queryKey: postRowsQueryKey })
  return interrupted
}

export const resumePostRows = (
  client: QueryClient,
  interrupted: QueryKey[] = []
): void => {
  interrupted.forEach((queryKey) =>
    client.invalidateQueries(
      { queryKey, exact: true },
      { cancelRefetch: false }
    )
  )
}

export const invalidatePublishedPost = (
  client: QueryClient,
  postId?: string
): void => {
  client.invalidateQueries({ queryKey: postScheduledQueryKey })
  client.invalidateQueries({ queryKey: postFeedQueryKey })
  client.invalidateQueries({ queryKey: postPinnedQueryKey })
  if (postId) {
    client.invalidateQueries({ queryKey: postRowQueries.row(postId).queryKey })
  }
}

export const catchUpPublishedPosts = (client: QueryClient): void => {
  if (client.getQueryData(postScheduledQueryKey) !== undefined) {
    invalidatePublishedPost(client)
  }
}
